/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/api/client.ts
 *
 * Rôle :
 *   Wrapper HTTP léger autour de `fetch` pour parler aux endpoints PHP
 *   `api/*.php` côté serveur (cf. CLAUDE.md §10.12, §14.9). Gère :
 *     - en-tête `Accept: application/json`
 *     - en-tête `Accept-Language: fr-FR` (cohérent §15.5)
 *     - en-tête `X-Requested-With: TNE-2026` (anti-CSRF basique)
 *     - timeout via AbortController
 *     - parsing JSON sécurisé + typage générique
 *     - erreurs typées (`ApiError`) lisibles côté UI
 *
 * Réutilisable :
 *   100 %. Copier-coller dans NOLAS pour moderniser ses appels jQuery.ajax.
 *
 * @author Mika
 * @since 2026-05-04
 */

/** Erreur API typée, utilisée par TanStack Query pour les `error.message`. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

// ============================================================================
// FIX 2026-05-07 (PROD COMPLETE A1) : Gestion CSRF token
// ============================================================================
//
// Le serveur PHP (`_lib/auth.php::auth_csrf_check()`) exige le header
// `X-CSRF-Token` sur toutes les routes mutation sensibles (login.php
// step 2, email-2fa-verify.php, admin/*, etc.).
//
// Workflow :
//   1. Au boot, `bootstrapCsrfToken()` lit le token éventuel depuis
//      sessionStorage (résiste à F5 / fermeture onglet pendant la
//      session 7j sliding admin).
//   2. À chaque réponse JSON contenant un champ `csrf_token`, on capture
//      automatiquement la nouvelle valeur (pattern Symfony / Laravel
//      csrf-cookie-middleware), `extractCsrfFromResponse()`.
//   3. À chaque `apiMutate` (POST/PUT/PATCH/DELETE), on injecte le
//      token courant dans le header `X-CSRF-Token` si dispo
//      `injectCsrfHeader()`.
//   4. Les composants login peuvent forcer le set du token explicitement
//      via `setCsrfToken(token)` (utile pour LoginAdminPage juste après
//      step 1).
//
// Sécurité :
//   - On stocke en `sessionStorage` (pas localStorage) → token effacé
//     à la fermeture de l'onglet (sauf F5 où sessionStorage persiste).
//   - On ne stocke JAMAIS le mdp ni le code 2FA, uniquement le csrf_token
//     opaque retourné par le serveur.

const CSRF_STORAGE_KEY = 'tne.csrf.token';

/** Token CSRF courant en mémoire (source de vérité). */
let csrfToken: string | null = null;

/**
 * Hydrate le token CSRF depuis sessionStorage au boot. Appelé au premier
 * import du module (équivalent d'un init lazy).
 */
function bootstrapCsrfToken(): void {
  if (typeof window === 'undefined') return;
  try {
    const stored = window.sessionStorage.getItem(CSRF_STORAGE_KEY);
    if (typeof stored === 'string' && stored.length > 0) {
      csrfToken = stored;
    }
  } catch {
    // sessionStorage indispo (mode privé Safari très restrictif), on ignore.
  }
}
bootstrapCsrfToken();

/**
 * Setter explicite du token CSRF, utilisé par LoginAdminPage après le
 * succès de l'étape 1 (login.php) et par EmailTwoFactorPage après le
 * succès du verify (peut renvoyer un nouveau token rotaté).
 *
 * @param token Token reçu du serveur (string non-vide), ou null pour purger.
 */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
  if (typeof window === 'undefined') return;
  try {
    if (token === null || token.length === 0) {
      window.sessionStorage.removeItem(CSRF_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(CSRF_STORAGE_KEY, token);
    }
  } catch {
    // sessionStorage indispo, la mémoire `csrfToken` reste l'unique source.
  }
}

/**
 * Lit le token CSRF courant. Exporté pour tests + diagnostic admin.
 */
export function getCsrfToken(): string | null {
  return csrfToken;
}

/**
 * Inspecte une réponse JSON parsée et capture automatiquement le champ
 * `csrf_token` s'il est présent. Permet la rotation transparente du
 * token côté serveur (chaque réponse peut renvoyer un nouveau token).
 */
function extractCsrfFromResponse(parsed: unknown): void {
  if (
    parsed !== null &&
    typeof parsed === 'object' &&
    'csrf_token' in parsed &&
    typeof parsed.csrf_token === 'string'
  ) {
    const next = parsed.csrf_token;
    if (next.length > 0 && next !== csrfToken) {
      setCsrfToken(next);
    }
  }
}

/** Options de l'appel HTTP. */
export interface ApiRequestOptions {
  /** Méthode HTTP, défaut `GET`. */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Corps JSON sérialisable (ignoré en GET). */
  body?: unknown;
  /** En-têtes additionnels, fusionnés avec ceux par défaut. */
  headers?: Record<string, string>;
  /** Timeout en ms, défaut 8000. */
  timeoutMs?: number;
  /** Signal externe (priorité sur le timeout interne). */
  signal?: AbortSignal;
  /**
   * Capturer le `csrf_token` de la réponse comme jeton GLOBAL (défaut vrai).
   *
   * 2026-09-28 (2FA participants, revue adversariale, constat 2) : faux pour
   * le jeton de la session PARTICIPANT lu par `visitor/me/two-factor.php`.
   * Un administrateur connecté dans le même navigateur garde ainsi son jeton
   * global (celui de `me/role.php`) : le jeton participant vit dans le seul
   * parcours qui en a besoin, au lieu d'écraser celui d'une autre session.
   * Mikael - chef de projet - 2026-09-28
   */
  captureCsrf?: boolean;
}

/**
 * Base URL des endpoints, relative pour faciliter le déploiement FTP sous
 * un sous-dossier. Surchargée via `VITE_API_BASE_URL` si besoin.
 */
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

/**
 * Lance une requête HTTP typée vers un endpoint API TNE.
 *
 * @param path Chemin relatif sous /api, ex. `'visitors.php?id=42'`.
 * @param options Options de l'appel (cf. {@link ApiRequestOptions}).
 * @returns La réponse JSON désérialisée, typée par le générique appelant.
 * @throws {ApiError} si statut HTTP non 2xx ou JSON invalide.
 */

/**
 * Message d'erreur lisible à partir d'une réponse d'API en échec.
 *
 * 🔴 FIX 2026-09-09 (Mika, capture du Form builder), « Erreur HTTP 422 sur
 * /api/admin/form-schema.php » s'affichait alors que le serveur renvoyait, à
 * côté, la liste EXACTE de ce qui n'allait pas. Le client ne lisait que
 * `message` : tout endpoint répondant `error` + `issues`, la convention la
 * plus répandue du projet, voyait son explication jetée.
 *
 * Un refus sans motif oblige la personne à deviner. Le serveur avait fait son
 * travail ; c'est le client qui perdait l'information.
 *
 * Ordre de préférence : `message` (le plus rédigé), puis `error`, puis le
 * repli technique. Les `issues` (ou `errors`) sont AJOUTÉES au message, car
 * ce sont elles qui disent quoi corriger.
 */
function messageDErreur(parsed: unknown, status: number, url: string): string {
  if (parsed === null || typeof parsed !== 'object') {
    return `Erreur HTTP ${status} sur ${url}`;
  }
  const o = parsed as Record<string, unknown>;

  let base =
    typeof o.message === 'string' && o.message !== ''
      ? o.message
      : typeof o.error === 'string' && o.error !== ''
        ? o.error
        : `Erreur HTTP ${status} sur ${url}`;

  // `issues` (form-schema) ou `errors` (validation générique) : une liste de
  // chaînes disant précisément quoi corriger.
  const brut = Array.isArray(o.issues) ? o.issues : Array.isArray(o.errors) ? o.errors : null;
  if (brut !== null) {
    const details = brut.filter((x): x is string => typeof x === 'string' && x !== '');
    if (details.length > 0) {
      // Au-delà de trois, on annonce le reste plutôt que de noyer la personne
      // sous un mur de texte dans une notification.
      const montres = details.slice(0, 3);
      const reste = details.length - montres.length;
      base += `\n• ${montres.join('\n• ')}`;
      if (reste > 0) {
        base += `\n…et ${String(reste)} autre${reste > 1 ? 's' : ''}.`;
      }
    }
  }
  return base;
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const {
    method = 'GET',
    body,
    headers = {},
    timeoutMs = 8000,
    signal,
    captureCsrf = true,
  } = options;

  // Timeout interne via AbortController, sauf si le caller fournit son propre signal.
  const controller = new AbortController();
  const timer = signal
    ? null
    : window.setTimeout(() => controller.abort(new Error('timeout')), timeoutMs);

  const url = path.startsWith('http') ? path : `${API_BASE_URL}/${path.replace(/^\//, '')}`;

  try {
    const res = await fetch(url, {
      method,
      headers: {
        Accept: 'application/json',
        'Accept-Language': 'fr-FR',
        'X-Requested-With': 'TNE-2026',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
      signal: signal ?? controller.signal,
    });

    // 204 No Content → renvoyer null typé (côté caller, T = null | …)
    if (res.status === 204) {
      return null as T;
    }

    const text = await res.text();
    let parsed: unknown = null;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        // Réponse non-JSON, on renvoie le texte brut dans ApiError.payload.
        throw new ApiError(`Réponse non-JSON depuis ${url}`, res.status, text);
      }
    }

    if (!res.ok) {
      // FIX 2026-05-07 (PROD A1), capture CSRF même sur erreur (le
      // serveur peut renvoyer un nouveau token avec la 4xx, à utiliser
      // pour le retry).
      if (captureCsrf) extractCsrfFromResponse(parsed);
      throw new ApiError(messageDErreur(parsed, res.status, url), res.status, parsed);
    }

    // FIX 2026-05-07 (PROD A1) : Auto-capture du csrf_token éventuel
    // dans toute réponse JSON OK (rotation transparente côté serveur).
    if (captureCsrf) extractCsrfFromResponse(parsed);

    return parsed as T;
  } finally {
    if (timer !== null) {
      window.clearTimeout(timer);
    }
  }
}

// ============================================================================
// apiMutate, wrapper offline-first pour les MUTATIONS (POST/PUT/PATCH/DELETE)
// ============================================================================

/**
 * Résultat d'une mutation : soit OK avec une réponse serveur, soit "queued"
 * si on a basculé en queue offline IndexedDB.
 */
export type MutateResult<T> =
  | { kind: 'ok'; data: T; idempotencyKey: string }
  | { kind: 'queued'; idempotencyKey: string; queueId: string };

/** Options spécifiques à une mutation. */
export interface ApiMutateOptions extends Omit<ApiRequestOptions, 'method'> {
  /**
   * Catégorie métier, alimente le wording des toasts en cas d'échec
   * (cf. syncManager.humanKind). Ex. 'checkin', 'workshop_signup',
   * 'profile_update', 'consent', 'feedback'.
   */
  kind: string;
  /**
   * Si false, désactive la queue offline (la mutation échoue plutôt
   * qu'être différée). Utile pour le login (§18.5 garde-fou n° 2).
   */
  offline?: boolean;
  /**
   * Idempotency-Key explicite (sinon UUID v4 généré à la volée).
   * Permet à un caller de re-soumettre une mutation avec la même clé
   * pour confirmer la dédup côté serveur.
   */
  idempotencyKey?: string;
  /**
   * Jeton CSRF à envoyer À LA PLACE du jeton global, pour une écriture qui
   * relève d'une autre session que celle du jeton global (2026-09-28 : le
   * parcours « application » des participants, quand un administrateur est
   * aussi connecté dans ce navigateur). Réservé aux écritures `offline:false`
   * : une écriture mise en file d'attente repartirait avec le jeton global.
   * Mikael - chef de projet - 2026-09-28
   */
  csrfToken?: string;
  /**
   * Tente TOUJOURS l'envoi, même si `navigator.onLine` dit « hors ligne »
   * (indicateur non fiable sur iOS), et ne met en file qu'après un VRAI échec
   * réseau. Pour les scans à verdict immédiat qui acceptent quand même la
   * file (contrôle sans casier, 2026-10-05) : on garde le verdict tant que le
   * réseau répond, on ne perd plus le scan quand il ne répond pas.
   * Mikael - chef de projet - 2026-10-05
   */
  forceAttempt?: boolean;
}

/**
 * Lance une mutation POST/PUT/PATCH/DELETE avec **fallback queue offline**
 * (cf. CLAUDE.md §18 + §F13 du CDC).
 *
 * Comportement :
 *   1. Génère un `Idempotency-Key` (UUID), propagé en header.
 *   2. Tente fetch immédiat (TOUJOURS quand `offline === false`, cf. fix
 *      ci-dessous ; sinon, pré-skip possible si `navigator.onLine === false`).
 *   3. Si réseau down (TypeError fetch) ET `offline !== false` :
 *        - enqueue dans IndexedDB via `offline/queue.ts`
 *        - renvoie `{ kind: 'queued', idempotencyKey, queueId }`
 *      → l'UI peut afficher « Action en attente, sera synchronisée ».
 *   4. Si fetch réussit (2xx) → renvoie `{ kind: 'ok', data, idempotencyKey }`.
 *   5. Si fetch renvoie 4xx/5xx → throw `ApiError` (pas de queue, c'est une
 *      erreur définitive, pas une coupure réseau).
 *   6. Si `offline === false` ET erreur réseau (TypeError) → throw l'erreur
 *      brute (pas d'enqueue, l'opération exige un verdict immédiat).
 *
 * FIX 2026-05-19 (BUG /controle scan iOS : CLAUDE.md §5/§24) :
 *   Pour `offline === false`, on TENTE TOUJOURS le fetch, on NE PRE-SKIP
 *   PAS sur `navigator.onLine === false`. Raison : `navigator.onLine` est
 *   notoirement non fiable (cf. §18.6, §10.1, useOnlineStatus.ts), peut
 *   être `false` sur iOS Safari / PWA standalone / venue WiFi alors que
 *   la connexion existe. Pour une opération à verdict immédiat
 *   (scan/contrôle/login/2FA/save backoffice, tous offline:false),
 *   pré-skipper sur ce signal non fiable produit un échec déterministe
 *   FAUX. L'autoritatif est le résultat RÉEL du fetch (TypeError =
 *   vraiment offline). Aligne le code avec §18.6 documenté. Scoped :
 *   offline:true conserve l'enqueue préemptif (§18 offline-first inchangé
 *   pour checkins/signups/profile etc.).
 *
 * @example
 *   const r = await apiMutate<{ ok: boolean }>('checkins.php', 'POST',
 *     { stand_id: 12, visitor_id: 42 },
 *     { kind: 'checkin' });
 *   if (r.kind === 'queued') toast.info('Pointage enregistré hors ligne');
 *   else                     toast.success('Pointage validé');
 *
 * @author Mika
 */
export async function apiMutate<T>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body: unknown,
  options: ApiMutateOptions,
): Promise<MutateResult<T>> {
  const idempotencyKey = options.idempotencyKey ?? makeUuid();

  // Détection offline préliminaire, `navigator.onLine` est non fiable mais
  // permet d'éviter un fetch qui échouera de toute façon (pour offline:true).
  const knownOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

  // FIX 2026-05-19 (BUG /controle scan iOS : CLAUDE.md §5/§24) :
  // Pour les opérations à VERDICT IMMÉDIAT (offline:false explicite, scan,
  // contrôle, login, 2FA, save backoffice), on tente TOUJOURS le fetch.
  // `navigator.onLine === false` est notoirement non fiable (cf. §18.6,
  // §10.1, useOnlineStatus.ts), peut être false sur iOS Safari alors que
  // la connexion existe. Pre-skipper produit un échec déterministe FAUX
  // (bug /controle scan iOS : POST scan.php jamais émis, organisateur figé).
  // L'autoritatif = résultat RÉEL du fetch (TypeError = vraiment offline).
  // Scoped : offline:true conserve `knownOffline` (§18 offline-first
  // inchangé pour checkins/signups/profile).
  const forceAttempt = options.offline === false || options.forceAttempt === true;

  if (forceAttempt || !knownOffline) {
    try {
      // FIX 2026-05-07 (PROD A1), injection automatique du
      // header X-CSRF-Token sur toute mutation. Le serveur PHP exige
      // ce header sur les routes auth/* + admin/* sensibles
      // (`auth_csrf_check()` dans `_lib/auth.php`).
      const mergedHeaders: Record<string, string> = {
        ...(options.headers ?? {}),
        'Idempotency-Key': idempotencyKey,
      };
      // 2026-09-28 : un jeton EXPLICITE (celui de la session participant,
      // cf. `csrfToken` des options) passe avant le jeton global.
      // Mikael - chef de projet - 2026-09-28
      const jeton = options.csrfToken ?? csrfToken;
      if (jeton !== null) {
        mergedHeaders['X-CSRF-Token'] = jeton;
      }
      const data = await apiRequest<T>(path, {
        ...options,
        method,
        body,
        headers: mergedHeaders,
      });
      return { kind: 'ok', data, idempotencyKey };
    } catch (err) {
      // Si c'est une vraie erreur HTTP (4xx/5xx), on relance, pas de queue.
      // Seules les erreurs de réseau (TypeError) basculent en queue.
      if (err instanceof ApiError) {
        throw err;
      }
      // TypeError fetch ou abort = réseau down → on tombe dans la queue.
      if (options.offline === false) {
        throw err;
      }
      // Sinon on continue vers l'enqueue ci-dessous.
    }
  }

  // Cas offline (ou erreur réseau ci-dessus) : on enqueue.
  // FIX 2026-05-19 (BUG /controle scan iOS) : ce bloc est désormais
  // UNREACHABLE pour `offline === false` (forceAttempt force l'entrée
  // dans le try ci-dessus, et toute erreur y est re-thrown). Conservé
  // comme filet défensif au cas où un futur refactor change le flux.
  if (options.offline === false) {
    throw new ApiError(
      'Connexion réseau indisponible et offline désactivé pour cette mutation.',
      0,
    );
  }

  // Import dynamique pour ne pas alourdir le bundle initial des callers
  // qui n'utilisent que apiRequest (lectures).
  const { enqueue } = await import('@/offline/queue');
  const item = await enqueue({
    method,
    path,
    body,
    kind: options.kind,
    idempotencyKey,
  });

  // Le compteur affiche a l'ecran doit suivre la file MEME hors ligne : sans
  // cet appel, il reste fige pendant toute la coupure et la banniere « N scans
  // en attente » ne dit jamais la verite. Import dynamique pour la meme raison
  // que ci-dessus. Mikael - chef de projet - 2026-09-11
  try {
    const { refreshPendingCount } = await import('@/offline/syncManager');
    await refreshPendingCount();
  } catch {
    // Un compteur d'affichage ne fait jamais echouer une mise en file.
  }

  return { kind: 'queued', idempotencyKey, queueId: item.id };
}

/** Génère un UUID v4, fallback si crypto.randomUUID indispo. */
function makeUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ──────────────────────────────────────────────────────────────────────────
// Idées d'évolution (Mika) :
//  - Ajouter un retry exponentiel pour les 503/504 (Cloudflare cPanel).
//  - Brancher un interceptor pour redresser les sessions expirées (admin).
//  - Logger les erreurs 5xx vers le futur endpoint `/api/admin/audit.php`.
//  - Exposer un wrapper TanStack Query (`useApiMutation`) qui fait le
//    bridge avec apiMutate + toast automatique.
// Notes pour rétro-port NOLAS :
//  - Cette implémentation pure fetch peut remplacer les appels jQuery.ajax
//    dans les pages NOLAS (registration.html, scanner.html…). Le seul
//    boulot : remplacer chaque `$.ajax({...})` par `apiRequest(...)` /
//    `apiMutate(...)` selon le type d'opération.
//  - apiMutate apporte gratuitement à NOLAS l'offline-first pour les
//    check-ins (gros gain UX le jour de l'événement).
// ──────────────────────────────────────────────────────────────────────────
